import React, { useEffect, useRef, useState } from 'react';
import GenericDropdown from './GenericDropdown';

const MODE_OPTIONS = [
  { id: 'add', name: 'Add — always create (no dedup)' },
  { id: 'skip', name: 'Skip Duplicates — keep existing, add new' },
  { id: 'update', name: 'Update Duplicates — update existing, add new' },
  { id: 'sync', name: 'Sync — update existing, add new, delete missing' },
];

const KEY_METHOD_OPTIONS = [
  { id: 'field', name: 'Field name' },
  { id: 'sql', name: 'SQL expression over raw' },
];

function initialMethod(dedupe) {
  if (dedupe?.unique_key_sql) return 'sql';
  if (dedupe?.unique_key) return 'field';
  return 'field';
}

function buildBlock(mode, keyMethod, uniqueKey, uniqueKeySql) {
  const block = { mode: mode || 'add' };
  if (mode !== 'add') {
    if (keyMethod === 'sql' && String(uniqueKeySql || '').trim()) {
      block.unique_key_sql = String(uniqueKeySql).trim();
    } else if (String(uniqueKey || '').trim()) {
      block.unique_key = String(uniqueKey).trim();
    }
  }
  return block;
}

/**
 * Group-level deduplication controls (source-agnostic).
 */
export default function GroupDedupeConfig({
  value = {},
  onChange,
  multiSourceWarning = false,
}) {
  const dedupe = value && typeof value === 'object' ? value : {};
  const dedupeMode = dedupe.mode || 'add';
  const dedupeUniqueKey = dedupe.unique_key || '';
  const dedupeUniqueKeySql = dedupe.unique_key_sql || '';
  const [mode, setMode] = useState(dedupeMode);
  const [keyMethod, setKeyMethod] = useState(initialMethod(dedupe));
  const [uniqueKey, setUniqueKey] = useState(dedupeUniqueKey);
  const [uniqueKeySql, setUniqueKeySql] = useState(dedupeUniqueKeySql);
  const skipEmit = useRef(false);

  useEffect(() => {
    skipEmit.current = true;
    setMode(dedupeMode);
    setKeyMethod(initialMethod({
      unique_key: dedupeUniqueKey,
      unique_key_sql: dedupeUniqueKeySql,
    }));
    setUniqueKey(dedupeUniqueKey);
    setUniqueKeySql(dedupeUniqueKeySql);
  }, [dedupeMode, dedupeUniqueKey, dedupeUniqueKeySql]);

  useEffect(() => {
    if (skipEmit.current) {
      skipEmit.current = false;
      return;
    }
    onChange?.(buildBlock(mode, keyMethod, uniqueKey, uniqueKeySql));
  }, [mode, keyMethod, uniqueKey, uniqueKeySql, onChange]);

  return (
    <div className="group-dedupe-config">
      <div className="form-row">
        <label>Deduplication</label>
        <div className="hint">
          Applies to every ingest into this group (Google Spreadsheet, API, Transformation, SQL Transformation, etc.).
          Prefer this over the deprecated per-source Mode controls. Default is <strong>Add</strong> (always create).
          New non-duplicates are always added in every mode.
        </div>
        <GenericDropdown
          items={MODE_OPTIONS}
          value={mode || 'add'}
          onChange={(id) => setMode(id || 'add')}
          placeholder="Dedup mode"
        />
      </div>

      {mode !== 'add' && (
        <div className="form-row">
          <label>Unique key <span className="required">*</span></label>
          <GenericDropdown
            items={KEY_METHOD_OPTIONS}
            value={keyMethod}
            onChange={(id) => setKeyMethod(id || 'field')}
            placeholder="Key method"
          />
          {keyMethod === 'field' && (
            <>
              <input
                type="text"
                value={uniqueKey}
                onChange={(e) => setUniqueKey(e.target.value)}
                placeholder="id"
                style={{ marginTop: 8 }}
              />
              <div className="hint">Top-level field on each entry&apos;s <code>raw</code> (e.g. <code>id</code>).</div>
            </>
          )}
          {keyMethod === 'sql' && (
            <>
              <textarea
                value={uniqueKeySql}
                onChange={(e) => setUniqueKeySql(e.target.value)}
                placeholder={"raw->>'id' || '-' || (raw->>'start')"}
                rows={3}
                style={{ marginTop: 8, width: '100%', fontFamily: 'monospace' }}
              />
              <div className="hint">
                Expression over <code>raw</code>: <code>raw-&gt;&gt;&apos;field&apos;</code>, nested paths,
                literals, <code>||</code>, <code>COALESCE</code>.
              </div>
            </>
          )}
        </div>
      )}

      {mode === 'add' && (
        <div className="form-row">
          <div className="hint">No unique key required. Every ingest creates new entries.</div>
        </div>
      )}

      {mode === 'sync' && multiSourceWarning && (
        <div className="alert alert-error" style={{ marginTop: 8 }}>
          <i className="fas fa-exclamation-triangle" /> Sync deletes entries whose key is absent from the
          latest ingest. With multiple sources feeding this group, one source&apos;s Sync can remove another
          source&apos;s rows.
        </div>
      )}
    </div>
  );
}
